import { supabase } from "@/lib/supabase-client";
import { normaliseGender } from "@/lib/gender";

export interface PlayerAccount {
  id: string;
  auth_user_id: string;
  player_id: number | null;
  first_name: string | null;
  last_name: string | null;
  alias: string | null;
  gender: string | null;
  preferred_side: "left" | "right" | null;
  /** Can also play the other side if needed; only meaningful with a preferred side. */
  can_switch_side: boolean;
  whatsapp: string | null;
  avatar_url: string | null;
  country_code: string | null;
  home_club_id: number | null;
  playtomic_url: string | null;
  jungle_padel_email: string | null;
  kit_size: string | null;
  kit_width_cm: number | null;
  kit_length_cm: number | null;
  signup_source: { community?: string; door: string } | null;
  onboarding_completed_at: string | null;
  notifications: {
    event_invites: boolean;
    results: boolean;
    elo_changes: boolean;
    announcements: boolean;
  };
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

/** Fetch the player_account row for the currently signed-in user. Returns null if none exists. */
export async function fetchMyPlayerAccount(): Promise<PlayerAccount | null> {
  const { data, error } = await supabase
    .from("player_accounts")
    .select("*")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as PlayerAccount | null;
}

/** Create a player_accounts row linking the current user to a player record. */
export async function claimPlayer(
  playerId: number,
  playerName: string
): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  // players.players already records a gender for most production players. Carry it over so
  // /welcome does not ask a question we already know the answer to - but never guess: a value
  // we can't normalise (including "Unspecified") becomes null, so /welcome still asks.
  const { data: player } = await supabase
    .schema("players")
    .from("players")
    .select("gender")
    .eq("id", playerId)
    .maybeSingle();

  const { error } = await supabase.from("player_accounts").insert({
    auth_user_id: user.id,
    player_id: playerId,
    alias: playerName,
    gender: normaliseGender((player as { gender?: string | null } | null)?.gender),
  });

  if (error) {
    if (error.code === "23505") {
      if (error.message.includes("player_accounts_auth_user_id_key"))
        return { error: "You already have a player account. Sign out and sign back in." };
      return { error: "This player has already been claimed. Contact your organiser." };
    }
    return { error: error.message };
  }

  return { error: null };
}

/**
 * Pure function — finds unclaimed players whose first name token matches the
 * first name token of the just-claimed player. Used to surface merge suggestions.
 * Excludes the claimed player itself.
 */
export function findMergeCandidates<T extends { id: number; name: string }>(
  claimedName: string,
  candidates: T[]
): T[] {
  const claimedFirst = claimedName.trim().split(/\s+/)[0].toLowerCase();
  return candidates.filter((p) => {
    const pFirst = p.name.trim().split(/\s+/)[0].toLowerCase();
    return pFirst === claimedFirst && p.name.toLowerCase() !== claimedName.toLowerCase();
  });
}

/** Submit a merge request for organizer review. Never auto-executes. */
export async function submitMergeRequest(
  primaryPlayerId: number,
  duplicatePlayerId: number
): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  const { error } = await supabase.from("player_merge_requests").insert({
    requested_by: user.id,
    primary_player_id: primaryPlayerId,
    duplicate_player_id: duplicatePlayerId,
  });
  return { error: error?.message ?? null };
}

/** Update fields on the current user's player_accounts row. */
export async function updatePlayerAccount(
  updates: Partial<Pick<PlayerAccount,
    | "first_name" | "last_name" | "alias" | "gender" | "preferred_side" | "can_switch_side" | "whatsapp" | "avatar_url" | "notifications"
    | "country_code" | "home_club_id" | "playtomic_url" | "signup_source" | "onboarding_completed_at"
    | "jungle_padel_email" | "kit_size" | "kit_width_cm" | "kit_length_cm">>
): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  const { error } = await supabase
    .from("player_accounts")
    .update(updates)
    .eq("auth_user_id", user.id);

  return { error: error?.message ?? null };
}

/**
 * Like updatePlayerAccount, but creates the row when the user has none yet —
 * a player who skipped the claim step has an auth user and no player_accounts row.
 */
export async function upsertPlayerAccount(
  updates: Parameters<typeof updatePlayerAccount>[0]
): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  const { error } = await supabase
    .from("player_accounts")
    .upsert({ auth_user_id: user.id, ...updates }, { onConflict: "auth_user_id" });

  return { error: error?.message ?? null };
}

/** Set player_id to null (unlink). User is returned to /claim. */
export async function unlinkPlayerAccount(): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  const { error } = await supabase
    .from("player_accounts")
    .update({ player_id: null, alias: null })
    .eq("auth_user_id", user.id);
  return { error: error?.message ?? null };
}

/** Look up an invite by token. Returns null if already accepted. */
export async function fetchInviteByToken(
  token: string
): Promise<{ player_id: number; email: string } | null> {
  const { data, error } = await supabase
    .from("player_invites")
    .select("player_id, email, accepted_at")
    .eq("token", token)
    .maybeSingle();
  if (error || !data || data.accepted_at) return null;
  return { player_id: data.player_id, email: data.email };
}

/** Accept an invite: create player_accounts row + mark invite as accepted. */
export async function acceptInvite(token: string): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };

  const invite = await fetchInviteByToken(token);
  if (!invite) return { error: "Invite not found or already used." };

  const { data: player } = await supabase
    .schema("players")
    .from("players")
    .select("name, gender")
    .eq("id", invite.player_id)
    .maybeSingle();

  const { error: insertError } = await supabase.from("player_accounts").insert({
    auth_user_id: user.id,
    player_id: invite.player_id,
    alias: player?.name ?? null,
    gender: normaliseGender((player as { gender?: string | null } | null)?.gender),
  });

  if (insertError) {
    if (insertError.code === "23505") return { error: "An account is already linked to this player. Contact your organiser." };
    return { error: insertError.message };
  }

  await supabase
    .from("player_invites")
    .update({ accepted_at: new Date().toISOString() })
    .eq("token", token);

  return { error: null };
}

export interface ClaimablePlayer {
  id: number;
  name: string;
  alias: string;
  match_count: number;
  last_played: string | null;
  claimed: boolean;
  in_community: boolean;
}

/** Name-first claim search. Empty query → [] without a round trip. */
export async function searchClaimablePlayers(
  query: string,
  communityId?: string | null
): Promise<ClaimablePlayer[]> {
  const q = query.trim();
  if (!q) return [];
  const { data, error } = await supabase.rpc("search_claimable_players", {
    p_query: q,
    p_community_id: communityId ?? null,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as ClaimablePlayer[];
}

/** "I'm new here": create a player row and link it to the signed-in account. */
export async function claimNewPlayer(
  name: string,
  gender?: string | null,
  level?: string | null
): Promise<{ playerId: number | null; error: string | null }> {
  const { data, error } = await supabase.rpc("claim_new_player", {
    p_name: name,
    p_gender: gender ?? "Unspecified",
    p_level: level ?? null,
  });
  if (error) {
    if (error.message.includes("already linked")) return { playerId: null, error: "This account is already linked to a player." };
    if (error.message.includes("level")) return { playerId: null, error: "Pick a playing level from the list." };
    if (error.message.includes("1-100")) return { playerId: null, error: "Enter a name between 1 and 100 characters." };
    return { playerId: null, error: "Something went wrong. Try again in a moment." };
  }
  return { playerId: Number(data), error: null };
}

/** "That's me but it's taken": leave a note for the organiser. */
export async function submitClaimNote(playerId: number, note?: string): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: "Not signed in" };
  const { error } = await supabase.from("player_claim_notes").insert({
    requested_by: user.id,
    player_id: playerId,
    note: note ?? null,
  });
  return { error: error ? "Something went wrong. Try again in a moment." : null };
}

export interface Club { club_id: number; club_name: string; area: string | null }

/** Active clubs for the home-club picker, grouped by area on the client. */
export async function fetchActiveClubs(): Promise<Club[]> {
  const { data, error } = await supabase
    .schema("venues")
    .from("padel_clubs")
    .select("club_id, club_name, area")
    .eq("status", "active")
    .order("area", { ascending: true, nullsFirst: false })
    .order("club_name");
  if (error) throw new Error(error.message);
  return (data ?? []) as Club[];
}

/** Matches on record for a player, from the ELO dimension view. 0 when unknown. */
export async function fetchPlayerMatchCount(playerId: number): Promise<number> {
  const { data } = await supabase
    .schema("dimension_tables")
    .from("dim_players_with_elo")
    .select("matches_played")
    .eq("player_id_nk", String(playerId))
    .maybeSingle();
  return Number((data as { matches_played?: number } | null)?.matches_played ?? 0);
}
